import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  ChartBarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

// Color palette for charts
const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'];
const STATUS_COLORS = {
  open: '#22c55e',
  closed: '#ef4444',
  filled: '#3b82f6',
  draft: '#9ca3af'
};

function Analytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [outreachData, setOutreachData] = useState(null);
  const [autopilotData, setAutopilotData] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);

  const fetchAllAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [overviewRes, outreachRes, autopilotRes, jobsRes, funnelRes] = await Promise.all([
        api.get('/api/analytics/overview'),
        api.get('/api/analytics/outreach', { params: { days: dateRange } }),
        api.get('/api/analytics/autopilot', { params: { days: dateRange } }),
        api.get('/api/analytics/jobs'),
        api.get('/api/analytics/funnel', { params: { days: dateRange } })
      ]);

      setOverview(overviewRes.data.data);
      setOutreachData(outreachRes.data.data);
      setAutopilotData(autopilotRes.data.data);
      setJobsData(jobsRes.data.data);
      setFunnelData(funnelRes.data.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAllAnalytics();
  }, [fetchAllAnalytics]);

  const StatCard = ({ title, value, subtext, icon: Icon, trend, trendValue }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtext && <p className="text-sm text-gray-500 mt-1">{subtext}</p>}
        </div>
        <div className="flex flex-col items-end">
          <div className="p-3 bg-indigo-100 rounded-lg">
            <Icon className="h-6 w-6 text-indigo-600" />
          </div>
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? (
                <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
              )}
              <span>{trendValue}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-red-600 text-lg">{error}</p>
        <button
          onClick={fetchAllAnalytics}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your recruitment performance and metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(parseInt(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Candidates"
            value={overview.candidates.total}
            subtext={`${overview.candidates.last30Days} in last 30 days`}
            icon={UserGroupIcon}
            trend={overview.candidates.growthRate >= 0 ? 'up' : 'down'}
            trendValue={Math.abs(overview.candidates.growthRate)}
          />
          <StatCard
            title="Active Jobs"
            value={overview.jobs.active}
            subtext={`${overview.jobs.total} total jobs`}
            icon={BriefcaseIcon}
          />
          <StatCard
            title="Emails Sent"
            value={overview.autopilot.emailsSent}
            subtext={`${overview.autopilot.highMatches} high matches`}
            icon={EnvelopeIcon}
          />
          <StatCard
            title="Conversion Rate"
            value={`${overview.autopilot.conversionRate}%`}
            subtext="Autopilot to email sent"
            icon={ChartBarIcon}
          />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recruitment Funnel */}
        {funnelData && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recruitment Funnel</h3>
            <ResponsiveContainer width="100%" height={300}>
              <FunnelChart>
                <Tooltip />
                <Funnel
                  dataKey="count"
                  data={funnelData.funnel}
                  isAnimationActive
                >
                  <LabelList
                    position="right"
                    fill="#374151"
                    stroke="none"
                    dataKey="stage"
                  />
                  <LabelList
                    position="center"
                    fill="#fff"
                    stroke="none"
                    dataKey="count"
                    formatter={(value) => value}
                  />
                  {funnelData.funnel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Match Score Distribution */}
        {autopilotData && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Match Score Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={autopilotData.scoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]}>
                  {autopilotData.scoreDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={index >= 4 ? '#22c55e' : index >= 2 ? '#f59e0b' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Outreach Volume */}
        {outreachData && outreachData.dailyVolume.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Outreach Volume</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={outreachData.dailyVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Area
                  type="monotone"
                  dataKey="emailsSent"
                  stroke="#6366f1"
                  fill="#a5b4fc"
                  name="Emails Sent"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Autopilot Activity */}
        {autopilotData && autopilotData.dailyActivity.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Autopilot Activity</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={autopilotData.dailyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="processed"
                  stroke="#6366f1"
                  strokeWidth={2}
                  name="Processed"
                />
                <Line
                  type="monotone"
                  dataKey="highMatches"
                  stroke="#22c55e"
                  strokeWidth={2}
                  name="High Matches"
                />
                <Line
                  type="monotone"
                  dataKey="emailsSent"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Emails Sent"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Status Breakdown */}
        {jobsData && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Status</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={jobsData.statusBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="status"
                >
                  {jobsData.statusBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Outreach by Action */}
        {outreachData && outreachData.byAction.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Outreach by Action</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={outreachData.byAction}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ action, count }) => `${action}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="action"
                >
                  {outreachData.byAction.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Jobs Over Time */}
        {jobsData && jobsData.trendsOverTime.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Jobs Created</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={jobsData.trendsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Jobs Table */}
      {jobsData && jobsData.topJobs.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Jobs by Candidates</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Match Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emails Sent
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobsData.topJobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{job.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          job.status === 'open'
                            ? 'bg-green-100 text-green-800'
                            : job.status === 'filled'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.candidateCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{job.avgMatchScore}%</span>
                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              job.avgMatchScore >= 80
                                ? 'bg-green-500'
                                : job.avgMatchScore >= 60
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${job.avgMatchScore}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.emailsSent}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Funnel Trends */}
      {funnelData && funnelData.trends.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Funnel Trends Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={funnelData.trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="submissions"
                stackId="1"
                stroke="#6366f1"
                fill="#a5b4fc"
                name="Submissions"
              />
              <Area
                type="monotone"
                dataKey="qualified"
                stackId="2"
                stroke="#22c55e"
                fill="#86efac"
                name="Qualified"
              />
              <Area
                type="monotone"
                dataKey="contacted"
                stackId="3"
                stroke="#f59e0b"
                fill="#fcd34d"
                name="Contacted"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Empty State */}
      {(!funnelData?.funnel?.length && !autopilotData?.dailyActivity?.length) && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No data yet</h3>
          <p className="text-gray-500">
            Start processing candidates through the Autopilot workflow to see analytics here.
          </p>
        </div>
      )}
    </div>
  );
}

export default Analytics;
